import React, { FC } from "react";
import DayPicker, { DayPickerProps } from "react-day-picker";
import isSameDay from "date-fns/isSameDay";
import cloneDeep from "lodash/cloneDeep";
import { DayModifiers } from "react-day-picker/types/Modifiers";
import "./MultipleInlineCalendar.scss";
import useLocalValue from "../../hooks/useLocalValue";
import { useStore } from "effector-react";
import { $currentLocale } from "../../store/shared/locales";
import { useTranslation } from "react-i18next";
import { AppConfig } from "../../config";
import { isPresent } from "../../utils";

type Value = Date[] | null;

interface Props extends DayPickerProps {
  value?: Value;
  onChange?: (value: Value) => void;
  disabled?: boolean;
  maxValueLength?: number;
}
export type MultipleInlineCalendarProps = Props;

const MultipleInlineCalendar: FC<Props> = ({
  value: valueProp,
  onChange: onChangeProp,
  maxValueLength,
  ...props
}) => {
  const { t } = useTranslation(AppConfig.translationFileNames.react_day_picker);
  const currentLocale = useStore($currentLocale);
  const [displayValue, onChange] = useLocalValue(
    valueProp ?? null,
    [],
    onChangeProp
  );

  const handleDayClick = (day: Date, modifiers: DayModifiers) => {
    let clonedValue = cloneDeep(displayValue);
    if (clonedValue && (!modifiers.disabled || modifiers.selected)) {
      const dayIdx = clonedValue?.findIndex((value) => isSameDay(value, day));

      if (isPresent(dayIdx) && ~dayIdx) {
        clonedValue.splice(dayIdx, 1);
      } else {
        clonedValue.push(day);
      }
    }

    if (maxValueLength && clonedValue && clonedValue?.length > maxValueLength) {
      clonedValue = clonedValue.slice(clonedValue.length - maxValueLength);
    }

    onChange(clonedValue);
  };

  return (
    <DayPicker
      locale={currentLocale.key}
      months={t("MONTHS", { returnObjects: true })}
      weekdaysLong={t("WEEKDAYS_LONG", { returnObjects: true })}
      weekdaysShort={t("WEEKDAYS_SHORT", { returnObjects: true })}
      firstDayOfWeek={currentLocale.firstDayOfWeek}
      labels={t("LABELS", { returnObjects: true })}
      showOutsideDays
      numberOfMonths={1}
      selectedDays={displayValue ?? undefined}
      onDayClick={handleDayClick}
      renderDay={(date /*, modifiers*/) => (
        <div className="DayPicker-DayInner">{date.getDate()}</div>
      )}
      {...props}
    />
  );
};

export default MultipleInlineCalendar;
